import type { ScorecardData, ScorecardErrors } from '@kula-ai/components';
import type { ScorecardType } from '@/types/interview';
import type { ScorecardFillValues } from './scorecardFill';

/** Shown for a present-but-invalid answer (mirrors the BE's reject reason). */
export const INVALID_ANSWER_MESSAGE = 'This answer is invalid';

/**
 * FE mirror of the backend `AtsScorecard.is_valid_answer?`
 * (app/models/ats_scorecard.rb). The BE type-checks every *present* answer on
 * submit — even for non-mandatory questions — and 422s (`err_invalid_answer`)
 * when one doesn't match its type's shape/options. The kula-ai
 * `validateScorecard` only checks *required*, so we add this to catch the rest
 * client-side.
 */
export const isScorecardAnswerValid = (
  type: string | undefined,
  answer: unknown,
  options: string[] = []
): boolean => {
  switch (type) {
    case 'star':
      return (
        Number.isInteger(answer) &&
        (answer as number) >= 1 &&
        (answer as number) <= 5
      );
    case 'one_to_ten':
      return (
        Number.isInteger(answer) &&
        (answer as number) >= 1 &&
        (answer as number) <= 10
      );
    case 'yes_no':
      return answer === 'yes' || answer === 'no';
    case 'multiple_choice':
      return typeof answer === 'string' && options.includes(answer);
    case 'checkbox':
      return (
        Array.isArray(answer) &&
        answer.length > 0 &&
        answer.every((item) => options.includes(item as string))
      );
    case 'open_question':
      return typeof answer === 'string';
    default:
      return true;
  }
};

/**
 * Whether an answer would reach the BE's branch-2 type check. Mirrors the
 * combination of the FE payload (omits falsy `values[idx + 1]`) and Ruby's
 * `present?` (empty string / empty array are blank). `0` is never submitted.
 */
const isPresentForValidation = (answer: unknown): boolean => {
  if (answer === null || answer === undefined || answer === 0) return false;
  if (typeof answer === 'string') return answer.trim().length > 0;
  if (Array.isArray(answer)) return answer.length > 0;
  return Boolean(answer);
};

/**
 * Errors for questions whose *present* answer is invalid for its type — keyed
 * by 0-based index to match `ScorecardErrors` / the builder's `errors` prop.
 */
export const findInvalidAnswers = (data: ScorecardData): ScorecardErrors => {
  const questions: Record<number, string> = {};
  (data.questions ?? []).forEach((question, index) => {
    if (
      isPresentForValidation(question.answer) &&
      !isScorecardAnswerValid(question.type, question.answer, question.options)
    ) {
      questions[index] = INVALID_ANSWER_MESSAGE;
    }
  });
  return Object.keys(questions).length > 0 ? { questions } : {};
};

/** Merge two error maps; required errors win per-question (they're disjoint). */
export const mergeScorecardErrors = (
  required: ScorecardErrors,
  invalid: ScorecardErrors
): ScorecardErrors => {
  const questions = {
    ...(invalid.questions ?? {}),
    ...(required.questions ?? {}),
  };
  const merged: ScorecardErrors = {};
  if (Object.keys(questions).length > 0) merged.questions = questions;
  if (required.feedback) merged.feedback = required.feedback;
  if (required.rating) merged.rating = required.rating;
  return merged;
};

/**
 * Coerce the auto-fixable answers into shapes the BE accepts, so a field that
 * already looks correct in the UI isn't blocked:
 *  - `yes_no` → lowercase (`"No"` → `"no"`), e.g. drafts from before the builder
 *    started emitting canonical values;
 *  - `star` / `one_to_ten` numeric strings (`"4"`) → integers.
 * Types that can't be safely coerced (multiple_choice / checkbox option text)
 * are surfaced by {@link findInvalidAnswers} instead.
 */
export const normalizeScorecardAnswers = (
  values: ScorecardFillValues,
  scorecard: ScorecardType
): ScorecardFillValues => {
  const next: ScorecardFillValues = { ...values };
  (scorecard?.questions ?? []).forEach((question, idx) => {
    const key = idx + 1;
    const raw = next[key];
    if (raw === null || raw === undefined || raw === '') return;
    if (question.type === 'yes_no' && typeof raw === 'string') {
      next[key] = raw.toLowerCase();
    } else if (
      (question.type === 'star' || question.type === 'one_to_ten') &&
      typeof raw === 'string' &&
      raw.trim() !== '' &&
      Number.isFinite(Number(raw))
    ) {
      next[key] = Math.round(Number(raw));
    }
  });
  return next;
};
